import { useState } from "react";
import axios from "axios";
import { Truck } from "lucide-react";
import { toast } from "sonner";
import { API, auth } from "./shared";

export const ShipPanel = ({ o, onShipped }) => {
    const [rates, setRates] = useState(null);
    const [courier, setCourier] = useState("");
    const [busy, setBusy] = useState(false);
    const [track, setTrack] = useState(null);
    const canShip = o.pincode && o.city && o.state;

    const loadRates = async () => {
        setBusy(true);
        try {
            const { data } = await axios.get(`${API}/admin/orders/${o.id}/rates`, auth());
            setRates(data.couriers);
            if (data.couriers[0]) setCourier(String(data.couriers[0].courier_id));
        } catch (err) {
            toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not fetch rates");
        } finally {
            setBusy(false);
        }
    };

    const ship = async () => {
        if (!window.confirm("Create shipment with Shiprocket? This books the courier and charges your Shiprocket wallet.")) return;
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/admin/orders/${o.id}/ship`, { courier_id: courier ? Number(courier) : null }, auth());
            onShipped(o.id, data);
            toast.success(`Shipped via ${data.courier} · AWB ${data.awb_code}`);
        } catch (err) {
            toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Shipment failed");
        } finally {
            setBusy(false);
        }
    };

    const doTrack = async () => {
        setBusy(true);
        try {
            const { data } = await axios.get(`${API}/admin/orders/${o.id}/track`, auth());
            setTrack(data);
        } catch {
            toast.error("Tracking unavailable");
        } finally {
            setBusy(false);
        }
    };

    if (o.shipment?.awb_code) {
        return (
            <div className="md:col-span-3 border-t border-gold/20 pt-4 text-sm" data-testid={`admin-shipment-${o.id}`}>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    <span className="flex items-center gap-2 text-charcoal"><Truck size={14} strokeWidth={1.5} className="text-golddark" /> {o.shipment.courier}</span>
                    <span className="text-smoke">AWB <span className="text-charcoal font-medium">{o.shipment.awb_code}</span></span>
                    <a href={o.shipment.tracking_url} target="_blank" rel="noopener noreferrer" className="text-golddark underline underline-offset-4 hover:text-charcoal">Tracking page</a>
                    <button data-testid={`admin-track-${o.id}`} onClick={doTrack} disabled={busy} className="text-[10px] tracking-[0.2em] uppercase border border-gold/40 px-3 py-1.5 hover:bg-gold hover:text-ivory transition-colors disabled:opacity-50">
                        {busy ? "…" : "Check status"}
                    </button>
                    {track && <span className="text-charcoal" data-testid={`admin-track-status-${o.id}`}>{track.status}{track.etd ? ` · ETA ${track.etd}` : ""}</span>}
                </div>
                {track?.activities?.length > 0 && (
                    <ul className="mt-3 space-y-1 text-xs text-smoke">
                        {track.activities.slice(0, 4).map((a, i) => <li key={i}>{a.date} — {a.activity}{a.location ? ` (${a.location})` : ""}</li>)}
                    </ul>
                )}
            </div>
        );
    }

    if (!["paid", "cod_received"].includes(o.status)) return null;

    return (
        <div className="md:col-span-3 border-t border-gold/20 pt-4" data-testid={`admin-ship-panel-${o.id}`}>
            {!canShip ? (
                <p className="text-xs text-ash">No PIN code on this order — ship manually or ask the customer for their PIN.</p>
            ) : rates === null ? (
                <button data-testid={`admin-rates-${o.id}`} onClick={loadRates} disabled={busy} className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase border border-gold/40 text-golddark px-4 py-2.5 hover:bg-gold hover:text-ivory transition-colors disabled:opacity-50">
                    <Truck size={12} strokeWidth={1.5} /> {busy ? "Fetching rates…" : "Ship with Shiprocket"}
                </button>
            ) : (
                <div className="flex flex-wrap items-center gap-3">
                    <select data-testid={`admin-courier-select-${o.id}`} value={courier} onChange={(e) => setCourier(e.target.value)} className="bg-transparent border border-gold/40 text-xs text-charcoal px-3 py-2.5 outline-none focus:border-gold min-w-[260px]">
                        {rates.map((r) => (
                            <option key={r.courier_id} value={r.courier_id}>{r.name} — ₹{r.rate} · ETA {r.etd}</option>
                        ))}
                    </select>
                    <button data-testid={`admin-ship-${o.id}`} onClick={ship} disabled={busy || !courier} className="flex items-center gap-2 bg-charcoal text-goldlight text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 hover:bg-golddark hover:text-ivory transition-colors disabled:opacity-50">
                        <Truck size={12} strokeWidth={1.5} /> {busy ? "Booking…" : "Create shipment"}
                    </button>
                </div>
            )}
        </div>
    );
};
